import {
  first_value,
  normalize_text,
  object_field,
  path_value,
  to_number,
} from './view_case_details_core_helpers';
import {
  submitter_name_from_submitted_records,
} from './view_case_details_answer_helpers';

function full_name(first_name, last_name) {
  return [first_name, last_name].map(normalize_text).filter(Boolean).join(' ');
}

const LABEL_FIELDS = ['entity_type_name', 'entity_type_label', 'domain_name', 'domain', 'name', 'label'];
const ENTITY_TYPE_ID_PATHS = [['entity_type_id'], ['entity_type', 'id'], ['entity_type']];
const ENTITY_PROFILE_ID_PATHS = [
  ['entity_profile_id'],
  ['entity_profile', 'id'],
  ['profile_id'],
  ['profile', 'id'],
  ['entity_id'],
];
const SUBMITTER_FIELDS = [
  'submitted_by_name',
  'user_name',
  'user_full_name',
  'requestor_name',
  'requester_name',
  'applicant_name',
  'created_by_name',
];

function field_values(source = {}, fields = []) {
  return fields.map((field) => object_field(source, field));
}

function path_values(source = {}, paths = []) {
  return paths.map((path) => path_value(source, path));
}

export function object_label(value = {}) {
  return typeof value === 'object' && value !== null
    ? normalize_text(first_value(field_values(value, LABEL_FIELDS)))
    : normalize_text(value);
}

function application_form_application(application_form = {}) {
  return application_form?.application || application_form?.entity_application || {};
}

function application_form_form(application_form = {}) {
  return application_form?.form || {};
}

function domain_values(source = {}) {
  return [
    source?.entity_type_name,
    source?.entity_type_label,
    source?.domain_name,
    object_label(source?.entity_type),
    source?.domain,
  ];
}

export function application_form_domain_label(application_form = {}) {
  return normalize_text(first_value([
    ...domain_values(application_form),
    ...domain_values(application_form_application(application_form)),
    ...domain_values(application_form_form(application_form)),
  ]));
}

export function domain_label_from_application_forms(application_forms = []) {
  return normalize_text(application_forms.map(application_form_domain_label).find(Boolean));
}

const OPENED_DATE_FIELDS = [
  'date_created',
  'created_at',
  'created_date',
  'createdAt',
  'application_date_created',
  'opened_at',
  'openedAt',
  'submitted_on',
  'submitted_at',
];

function opened_date_value(source = {}) {
  if (!source || typeof source !== 'object') return '';

  const direct_value = first_value(OPENED_DATE_FIELDS.map((field) => object_field(source, field)));
  if (direct_value) return direct_value;

  return Object.values(source)
    .filter((value) => value && typeof value === 'object')
    .map(opened_date_value)
    .find(Boolean) || '';
}

export function application_opened_at(application_form = {}) {
  return first_value([
    opened_date_value(application_form),
    opened_date_value(application_form_application(application_form)),
    opened_date_value(application_form?.entity_application || {}),
  ]);
}

export function application_opened_at_from_application_forms(application_forms = []) {
  return normalize_text(application_forms.map(application_opened_at).find(Boolean));
}

export function profile_opened_at(profile = {}) {
  return normalize_text(first_value([
    opened_date_value(profile),
    opened_date_value(profile?.entity_application || {}),
    opened_date_value(profile?.application || {}),
  ]));
}

function entity_type_id_value(source = {}) {
  return first_value(path_values(source, ENTITY_TYPE_ID_PATHS));
}

export function application_form_entity_type_id(application_form = {}) {
  return to_number(first_value([
    entity_type_id_value(application_form),
    entity_type_id_value(application_form_application(application_form)),
    entity_type_id_value(application_form_form(application_form)),
  ]));
}

export function entity_type_id_from_application_forms(application_forms = []) {
  return application_forms
    .map(application_form_entity_type_id)
    .find(Boolean) || null;
}

function entity_profile_source(application_form = {}) {
  const application = application_form_application(application_form);
  return application_form?.entity_profile
    || application_form?.profile
    || application?.entity_profile
    || application?.profile
    || {};
}

function entity_profile_id_values(source = {}) {
  return path_values(source, ENTITY_PROFILE_ID_PATHS);
}

export function application_form_entity_profile_id(application_form = {}) {
  const application = application_form_application(application_form);
  return to_number(first_value([
    ...entity_profile_id_values(application_form),
    ...entity_profile_id_values(application),
    entity_profile_source(application_form)?.id,
  ]));
}

export function entity_profile_id_from_application_forms(application_forms = []) {
  return application_forms
    .map(application_form_entity_profile_id)
    .find(Boolean) || null;
}

export function entity_type_label(entity_type = {}) {
  return normalize_text(first_value(field_values(entity_type, LABEL_FIELDS)));
}

export function person_label(value = {}) {
  return typeof value === 'object' && value !== null
    ? normalize_text(first_value([
        value?.name,
        value?.full_name,
        value?.user_full_name,
        full_name(value?.first_name, value?.last_name),
        value?.email,
        value?.username,
      ]))
    : normalize_text(value);
}

function submitter_values(source = {}) {
  return [
    ...field_values(source, SUBMITTER_FIELDS),
    full_name(source?.user_first_name, source?.user_last_name),
    person_label(source?.submitted_by),
    person_label(source?.requestor),
    person_label(source?.requester),
    person_label(source?.applicant),
    person_label(source?.created_by),
  ];
}

export function application_form_submitter_name(application_form = {}) {
  return normalize_text(first_value([
    ...submitter_values(application_form),
    ...submitter_values(application_form_application(application_form)),
  ]));
}

export function submitter_name_from_application_forms(application_forms = []) {
  return normalize_text(
    application_forms
      .map(application_form_submitter_name)
      .find(Boolean)
  ) || submitter_name_from_submitted_records(application_forms);
}

export function profile_submitter_name(profile = {}) {
  return normalize_text(first_value(submitter_values(profile)));
}

export function profile_case_description(profile = {}) {
  return normalize_text(first_value([
    profile?.entity_name,
    profile?.name,
    profile?.profile_name,
    profile?.title,
    profile?.description,
  ]));
}
